<template>
  <div v-if="revision == undefined" class="flex items-center justify-center h-full">
    <ProgressSpinner/>
  </div>
  <div v-else class="grid grid-cols-10 gap-5 max-w-7xl mx-auto px-6 py-6 flex-col items-center content-center">
    <div class="col-span-10 h-full">
      <Card>
        <template #title>
          {{
            $t('pages.firmware.firmware_overview.firmware_for_revision_x', {revision: revision.name })
          }}
          <Divider />
        </template>
        <template #content>
          <div class="-mt-6 content-center">
            <div class="flex flex-row w-full items-center" v-if="firmwaresStore.getFirmwareCount == 0">
                <IconAlertTriangleFilled class="h-5 w-5 mr-2 text-amber-500"/>
              <span class="text-sm text-slate-700">
                {{ $t('pages.firmware.firmware_overview.no_firmware_for_revision_x', {revision: revision.name }) }}
              </span>
            </div>
            <div v-else v-for="(firmware, index) in firmwaresStore.getFirmwares">
              <div :class="[index === 0 ? 'border-yellow-500 dark:bg-surface-600' : 'border-transparent ', 'transition-colors border-l-4 flex items-center justify-between gap-x-6 py-5 pl-3 pr-4 bg-surface-0 dark:bg-surface-800']">
                <div class="min-w-0 flex-row w-full items-start justify-between grid grid-cols-11">
                  <div class="flex-1 grid col-span-5 md:col-span-4">
                    <div class="flex items-start gap-x-3">
                      <p data-cy="firmware-version-number-display" class="text-sm font-semibold leading-6 text-surface-900 dark:text-surface-200">
                        {{ firmware.version_number }}</p>
                    </div>
                    <div class="flex items-start gap-x-3">
                      <p class="text-sm font-medium leading-6 text-surface-800 dark:text-surface-200">
                        {{ firmware.firmware_type?.name }}</p>
                    </div>
                    <div class="mt-1 flex items-center gap-x-2 text-xs leading-5 text-surface-500 dark:text-surface-400">
                      <p class="whitespace-nowrap">
                        {{ firmware.device_type?.name || '-' }} / {{ firmware.device_model?.name || '-' }}
                      </p>
                    </div>
                  </div>
                  <div class="grid grid-cols col-span-3 flex-1 mr-10 hidden md:block">
                    <span class="font-medium text-xs text-gray-700 mb-1">
                      {{ $t('pages.firmware.firmware_overview.regulations') }}:
                    </span>
                    <div class="flex items-start gap-1 flex-wrap">
                        <Tag :severity="'secondary'" v-if="firmware.regulations.length > 0"
                             v-for="regulation in firmware.regulations"
                             class="whitespace-nowrap">
                          <span>{{ regulation.name }}</span>
                        </Tag>
                      <span v-else>
                        <Tag class="bg-yellow-400 whitespace-nowrap">
                        <IconAlertTriangleFilled class="text-white-100 h-4 w-4 mr-1"/>
                          <span>
                            {{
                              $t('pages.firmware.firmware_overview.no_regulations')
                            }}
                          </span>
                        </Tag>
                      </span>
                    </div>
                    <span class="font-medium text-xs text-gray-700 mb-1 mt-5">
                      {{ $t('pages.firmware.firmware_overview.revisions') }}:
                    </span>
                    <div class="flex items-start gap-1 flex-wrap">
                      <Tag :severity="revision?.id === deviceRevision?.id ? 'info' : 'secondary'"
                           v-for="deviceRevision in firmware.device_revisions"
                           class="whitespace-nowrap">
                        <span>{{ deviceRevision.name }}</span>
                      </Tag>
                    </div>
                  </div>
                  <div class="flex-1 col-span-6 md:col-span-4 items-end space-y-1">
                    <div class="rounded-md border border-surface-300 bg-surface-0 dark:bg-surface-800 dark:border-surface-700 mt-2 truncate">
                      <ul role="list" class="divide-y divide-surface-300">
                        <li class="px-4 py-4 flex flex-row items-center space-x-2">
                          <FileTypeIcon class="mr-2" :mime-type="firmware.mime_type"/>
                          <div class="flex-1 shrink">
                            <h3 class="flex-auto truncate text-sm font-medium leading-4 text-surface-900 dark:text-surface-200">
                              {{ firmware.name }}
                            </h3>
                            <p class="block text-sm text-surface-600 dark:text-surface-400">
                              {{ useFilesize(firmware.size_bytes) }}
                            </p>
                            <span class="text-xs text-surface-600 dark:text-surface-400 flex flex-row truncate">
                              <IconAlertTriangleFilled class="text-yellow-400 h-4 w-4 mr-1" v-if="(firmware.release_notes_count ?? firmware.release_notes?.length ?? 0) == 0"/>
                              {{
                                $t('pages.firmware.firmware_overview.x_release_notes', firmware.release_notes_count ?? firmware.release_notes?.length ?? 0, {count: firmware.release_notes_count ?? firmware.release_notes?.length ?? 0})
                              }}
                            </span>
                          </div>
                          <div>
                            <Button type="button" @click="toggleFirmware($event, index)" severity="secondary"
                                    size="small" text aria-haspopup="true" aria-controls="overlay_menu">
                              <template #icon>
                                <IconDots/>
                              </template>
                            </Button>
                            <TieredMenu ref="firmwareMenu" id="overlay_menu" :model="getMenuItems(firmware)" :popup="true">
                              <template #itemicon="{ item, class: classes }">
                                <component v-if="item.icon" :is="item.icon" :classes="classes" class="mr-2"
                                           :mime-type="firmware.mime_type"/>
                              </template>
                            </TieredMenu>
                          </div>
                        </li>
                      </ul>
                    </div>
                    <div class="flex-1 gap-x-3 text-end	">
                      <p class="text-xs font-semibold leading-6 text-surface-900 dark:text-surface-200">
                        {{ $dateService(firmware.created_at).format('MMM DD, YYYY') }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <Divider />
            </div>
          </div>
        </template>
      </Card>
    </div>

  </div>
</template>

<style scoped>

</style>

<script>
import {mapStores} from "pinia";
import FormGroup from "@/Components/FormGroup.vue";
import {useDeviceRevisionsStore} from "@/stores/deviceRevisions.js";
import {useFirmwaresStore} from "@/stores/firmwares.js";
import FirmwarePropertiesForm from "@/Forms/Firmware/FirmwarePropertiesForm.vue";
import {useDownloadLink} from "@/Composables/DownloadLink.js";
import {useFilesize} from "@/Composables/Filesize.js";
import {IconAlertTriangleFilled, IconDots, IconFileCertificate, IconFileInfo, IconFileZip} from "@tabler/icons-vue";
import FileTypeIcon from "@/Components/FileTypeIcon.vue";
import FirmwareReleaseNotesList from "@/Forms/Firmware/FirmwareReleaseNotesList.vue";
import {useFirmwareReleaseNotesStore} from "@/stores/firmwareReleaseNote.js";
import {useDownloadResponse} from "../../Composables/DownloadLink.js";
import {resolveRevisionImageUrl} from "@/Composables/DeviceImage.js";

export default {
  name: "FirmwareOverviewView",
  components: {
    FirmwareReleaseNotesList,
    IconDots, FileTypeIcon,
    IconAlertTriangleFilled,
    IconFileCertificate,
    IconFileInfo,
    FirmwarePropertiesForm,
    FormGroup,
    IconFileZip,
  },
  data() {
    return {
      revision: undefined,
      params: {
        include: 'deviceType,deviceModel',
      },
      firmwareParams: {
        include: [
          'firmwareType',
          'deviceModel',
          'deviceType',
          'deviceRevisions',
          'releaseNotesCount',
          'testingDocumentsCount',
          'regulations',
          'releaseNotes',
        ],
        sort: '-created_at',
        filter: {
          device_revision_ids: [],
        }
      },
    }
  },
  methods: {
    useFilesize,
    toggleFirmware(e, index){
      this.$refs.firmwareMenu[index].toggle(e);

    },
    async refreshFirmwares(deviceRevisionId) {
      this.firmwareParams.filter.device_revision_ids = [deviceRevisionId];
      return this.firmwaresStore.indexFirmwareLookup(this.firmwareParams);
    },
    async downloadFirmware(firmwareId) {
      try {
        const response = await this.firmwaresStore.showFirmware(firmwareId, {download: 1});
        useDownloadLink(response.url);
      } catch (error) {
        console.log('Error downloading firmware', error);
      }
    },
    async downloadAll(firmware) {
      try {
        const data = await this.firmwaresStore.exportFirmwareForLookup(firmware.id, {with_testing_documents: 1, with_release_notes: 1});
        useDownloadResponse({ data }, firmware.version_number + "_export.zip");
      } catch(e) {
        console.error(e.response);
      }
    },
    async downloadFirmwareReleaseNote(firmwareReleaseNote) {
      if (firmwareReleaseNote) {
        try {
          const response = await this.firmwareReleaseNotesStore.downloadFirmwareReleaseNoteForLookup(firmwareReleaseNote.id);
          useDownloadLink(response.url);
        } catch (error) {
          console.log('Error downloading firmware release note', error);
        }
      }
    },
    getMenuItems(firmware) {
      let menu = []
      menu.push( { label: this.$t('forms.common.download'), icon: 'FileTypeIcon', command: (e) => { this.downloadFirmware(firmware.id)}});
      if (this.visibleReleaseNotes(firmware).length > 0) {
        menu.push({label: this.$t('pages.firmware.firmware_overview.release_notes'), icon: 'IconFileInfo', items: this.getReleaseNoteItems(firmware) });
      }
      menu.push( { label: this.$t('pages.firmware.firmware_overview.download_all_files'), icon: 'IconFileZip', command: (e) => { this.downloadAll(firmware)}});
      return menu ?? null;
    },
    getReleaseNoteItems(firmware) {
      let items = [];
      this.visibleReleaseNotes(firmware).forEach(release_note => {
        items.push({ label: release_note.name, command: (e) => { this.downloadFirmwareReleaseNote(release_note)} })
      })
      return items;
    },
    visibleReleaseNotes(firmware) {
      return (firmware?.release_notes ?? []).filter(releaseNote => {
        const filename = releaseNote?.name?.replaceAll('\\', '/').split('/').pop();
        return filename?.toLowerCase() !== 'addr_info.txt';
      });
    },
  },
  computed: {
    ...mapStores(useFirmwaresStore),
    ...mapStores(useDeviceRevisionsStore),
    ...mapStores(useFirmwareReleaseNotesStore),
    latestDeviceRevisionAvailableFirmwareId() {
      let firmwares = this.firmwaresStore.getFirmwares;
      if (firmwares) {
        let lastFirmware = firmwares.slice(-1).pop();
        return lastFirmware ? lastFirmware.id : undefined;
      }
      return undefined;
    },
    processedFirmwares() {
      return this.firmwaresStore.getFirmwares?.map(firmware => ({
        ...firmware,
        label: this.$t('pages.manufacturers.dashboard.manufacturers_firmware_update_dashboard_view.firmware_x_version_x', { firmware: firmware.name ?? this.$t('common.na'), version: firmware.version_number })
      })) || [];
    },
    currentTargetFirmwareErrorMessage() {
      if (this.latestDeviceRevisionAvailableFirmwareId !== this.approvedFirmwareForm.default_firmware_id) {
        return this.$t('pages.manufacturers.dashboard.manufacturers_firmware_update_dashboard_view.currently_not_tracking_the_latest_version');
      }
    },
    getImageUrl() {
      return resolveRevisionImageUrl(this.revision, 'https://placehold.co/600x400');
    },
  },
  watch: {
    '$route.params.revision': {
      async handler(newValue, oldValue) {
        if (newValue) {
          this.revision = null
          this.revision = await this.deviceRevisionsStore.showDeviceRevision(this.$route.params.revision, this.params);
          await this.refreshFirmwares(this.revision.id, this.firmwareParams);
        } else {
          this.revision = undefined;
        }
      },
      immediate: true,
    }
  },
}
</script>

<style scoped>

</style>
